import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/App';
import Navigation from '@/components/Navigation';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import axios from 'axios';
import {
  BadgeCheck,
  MapPin,
  Calendar,
  Settings,
  Edit3,
  Mountain,
  Tent,
  Waves,
  Camera,
  Users,
  Star,
  Trophy,
  ArrowRight
} from 'lucide-react';

import NotificationsCard from '@/components/NotificationsCard';
import AccountSecurityCard from '@/pages/profile/AccountSecurityCard';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const Profile = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, token, updateUser } = useAuth();
  const [badgeData, setBadgeData] = useState(null);

  // Scroll to the section referenced by `#<id>` when the URL contains a hash
  // (e.g. Settings → "Change Password" navigates here with `#account-security`).
  // React Router doesn't do this natively.
  useEffect(() => {
    if (!location.hash) return;
    const id = location.hash.slice(1);
    // Defer to next paint so the target element is mounted.
    const t = setTimeout(() => {
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
    return () => clearTimeout(t);
  }, [location.hash]);

  const fetchBadges = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/badges/user`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      setBadgeData(response.data);
    } catch (error) {
      console.error('Error fetching badges:', error);
    }
  }, [token]);

  useEffect(() => {
    fetchBadges();
  }, [fetchBadges]);

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const interestIcons = {
    hiking: Mountain,
    camping: Tent,
    kayaking: Waves,
    photography: Camera,
    social: Users
  };

  const experienceLevelColors = {
    beginner: 'bg-green-500/20 text-green-500',
    intermediate: 'bg-blue-500/20 text-blue-500',
    experienced: 'bg-purple-500/20 text-purple-500'
  };

  return (
    <div className="min-h-screen bg-background" data-testid="profile-page">
      <Navigation />
      
      <main className="px-4 pt-[68px] pb-8 sm:px-6 sm:pt-20 sm:pb-12 lg:px-8">
        <div className="max-w-4xl mx-auto">
          {/* Profile Header */}
          <div className="relative">
            {/* Banner */}
            <div className="h-48 rounded-3xl bg-gradient-to-br from-primary/20 via-accent/10 to-primary/20 overflow-hidden">
              <img
                src="https://images.unsplash.com/photo-1758272959663-b30513083206?w=1200&q=80"
                alt="Profile banner"
                className="w-full h-full object-cover opacity-30"
              />
            </div>
            
            {/* Avatar & Basic Info */}
            <div className="relative px-6 -mt-16">
              <div className="flex flex-col md:flex-row md:items-end gap-6">
                <Avatar className="h-32 w-32 border-4 border-background shadow-xl">
                  <AvatarImage src={user?.picture} alt={user?.name} />
                  <AvatarFallback className="text-3xl bg-primary text-primary-foreground">
                    {getInitials(user?.name)}
                  </AvatarFallback>
                </Avatar>
                
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-1">
                    <h1 className="text-2xl md:text-3xl font-bold">{user?.name}</h1>
                    <Badge variant="secondary" className="gap-1">
                      <BadgeCheck className="h-3 w-3" />
                      Verified
                    </Badge>
                  </div>
                  <p className="text-muted-foreground">{user?.email}</p>
                  {user?.location && (
                    <p className="flex items-center gap-1 text-muted-foreground mt-1">
                      <MapPin className="h-4 w-4" />
                      {user.location}
                    </p>
                  )}
                </div>
                
                <Button variant="outline" className="gap-2" onClick={() => navigate('/settings')} data-testid="edit-profile-btn">
                  <Edit3 className="h-4 w-4" />
                  Edit Profile
                </Button>
              </div>
            </div>
          </div>

          {/* Profile Content */}
          <div className="mt-12 grid md:grid-cols-3 gap-8">
            {/* Left Column - Stats & Experience */}
            <div className="space-y-6">
              {/* Notifications opt-in (always visible) */}
              <NotificationsCard />

              {/* Experience Level */}
              {user?.experience_level && (
                <div className="p-6 rounded-2xl bg-card border border-border/50">
                  <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide mb-3">Experience Level</h3>
                  <div className={`inline-flex items-center px-4 py-2 rounded-full font-medium ${experienceLevelColors[user.experience_level] || 'bg-muted'}`}>
                    <Star className="h-4 w-4 mr-2" />
                    {user.experience_level.charAt(0).toUpperCase() + user.experience_level.slice(1)}
                  </div>
                </div>
              )}

              {/* Quick Stats */}
              <div className="p-6 rounded-2xl bg-card border border-border/50">
                <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide mb-4">Stats</h3>
                <div className="space-y-4">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Quiz Completed</span>
                    <span className="font-medium">{user?.quiz_completed ? 'Yes' : 'No'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Member Since</span>
                    <span className="font-medium">2025</span>
                  </div>
                  {badgeData && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Badges Earned</span>
                      <span className="font-medium">{badgeData.badge_count} / {badgeData.total_available}</span>
                    </div>
                  )}
                  {badgeData && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Total Points</span>
                      <span className="font-medium text-amber-500">{badgeData.total_points} pts</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Badges Preview */}
              <div className="p-6 rounded-2xl bg-gradient-to-br from-amber-500/10 to-orange-500/10 border border-amber-500/20">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <Trophy className="h-5 w-5 text-amber-500" />
                    <h3 className="font-semibold">Badges</h3>
                  </div>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => navigate('/badges')}
                    className="text-amber-500 hover:text-amber-400"
                    data-testid="view-badges-btn"
                  >
                    View All <ArrowRight className="ml-1 h-4 w-4" />
                  </Button>
                </div>
                {badgeData?.earned_badges?.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {badgeData.earned_badges.slice(0, 4).map((badge) => (
                      <div
                        key={badge.id}
                        className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center shadow-lg"
                        title={badge.name}
                      >
                        <Star className="h-6 w-6 text-white" />
                      </div>
                    ))}
                    {badgeData.earned_badges.length > 4 && (
                      <div className="w-12 h-12 rounded-xl bg-muted flex items-center justify-center">
                        <span className="text-sm font-medium">+{badgeData.earned_badges.length - 4}</span>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    Complete activities to earn badges!
                  </p>
                )}
              </div>
            </div>

            {/* Right Column - Interests & Preferences */}
            <div className="md:col-span-2 space-y-6">
              {/* Interests */}
              {user?.interests?.length > 0 && (
                <div className="p-6 rounded-2xl bg-card border border-border/50">
                  <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide mb-4">Interests</h3>
                  <div className="flex flex-wrap gap-3">
                    {user.interests.map((interest) => {
                      const Icon = interestIcons[interest] || Mountain;
                      return (
                        <div
                          key={interest}
                          className="flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary"
                        >
                          <Icon className="h-4 w-4" />
                          <span className="font-medium capitalize">{interest.replace('_', ' ')}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Quiz Responses */}
              {user?.quiz_responses && (
                <div className="p-6 rounded-2xl bg-card border border-border/50">
                  <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide mb-4">Your Preferences</h3>
                  <div className="grid grid-cols-2 gap-4">
                    {user.quiz_responses.comfort_zones?.length > 0 && (
                      <div>
                        <p className="text-sm text-muted-foreground mb-2">Activities</p>
                        <div className="flex flex-wrap gap-2">
                          {user.quiz_responses.comfort_zones.map((zone) => (
                            <span key={zone} className="tag">{zone.replace('_', ' ')}</span>
                          ))}
                        </div>
                      </div>
                    )}
                    {user.quiz_responses.group_preference && (
                      <div>
                        <p className="text-sm text-muted-foreground mb-2">Group Preference</p>
                        <span className="tag">{user.quiz_responses.group_preference.replace('_', ' ')}</span>
                      </div>
                    )}
                    {user.quiz_responses.location_preference && (
                      <div>
                        <p className="text-sm text-muted-foreground mb-2">Travel Range</p>
                        <span className="tag capitalize">{user.quiz_responses.location_preference}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* CTA if no quiz */}
              {!user?.quiz_completed && (
                <div className="p-8 rounded-2xl bg-gradient-to-br from-primary/10 to-accent/10 border border-primary/20 text-center">
                  <h3 className="text-xl font-semibold mb-2">Complete Your Profile</h3>
                  <p className="text-muted-foreground mb-4">
                    Take our quick quiz to get personalized recommendations
                  </p>
                  <Button onClick={() => navigate('/quiz')} className="rounded-full" data-testid="profile-quiz-btn">
                    Take the Quiz
                  </Button>
                </div>
              )}

              {/* Account & Security */}
              <div id="account-security" className="scroll-mt-24">
                <AccountSecurityCard user={user} onEmailChanged={updateUser} />
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Profile;
